import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { IncomingMessage } from 'http';
import _mergeWith from 'lodash/mergeWith';
import { GetServerSidePropsContext } from 'next';
import { NextSeoProps } from 'next-seo';

import { MuseumOgpArtworkDocument } from '../../graphql/autogen/dato/hooks';
import { MediaFile } from '../../graphql/autogen/schemas';
import { BoothData } from '../../interfaces/museum';
import { findArtworkById } from '../../utils/artwork';
import { getLocalePath } from '../../utils/link';
import { absoluteUrl } from '../../utils/request';

const getPageUrl = (req?: IncomingMessage, asPath?: string) => {
  const { origin } = absoluteUrl(req);
  return `${origin}${asPath}`;
};

const defaultSeo: NextSeoProps = {
  title: 'Aniqueオンライン展覧会',
  description: '',
  openGraph: {
    type: 'website',
    locale: 'ja_JP',
    title: 'Aniqueオンライン展覧会',
    description: '',
    site_name: 'Anique',
    images: [
      {
        url: 'https://anique-assets.imgix.net/top-images/ogp/OGP_Anique_logo.jpg',
      },
    ],
  },
  twitter: {
    site: '@anique_jp',
    cardType: 'summary_large_image',
  },
};

const createNextSeoProps = (
  url: string,
  title?: string,
  mediaFile?: MediaFile,
  artworkImage?: string,
): NextSeoProps => {
  const { url: thumbnailUrl, width, height } = mediaFile ?? {};

  const ogpImage = artworkImage ?? thumbnailUrl;
  return _mergeWith(
    {},
    defaultSeo,
    {
      title,
      openGraph: {
        title,
        url,
        images: [
          {
            url: ogpImage,
            width,
            height,
          },
        ],
      },
      canonical: url,
    },
    (a, b) => (b === null ? a : undefined),
  );
};

export type SeoProps = {
  pageUrl: string;
  title?: string;
  thumbnailMedia?: MediaFile;
  nickName?: string;
  artworkImage?: string;
};

const getExhibitionSeoProps = async (
  apollo: ApolloClient<NormalizedCacheObject>,
  ctx: GetServerSidePropsContext,
): Promise<SeoProps | null> => {
  const { req, query, resolvedUrl, locale } = ctx;
  const localePath = getLocalePath(resolvedUrl, locale!);
  const pageUrl = getPageUrl(req, localePath);
  const artworkId = query.initArtwork as string;
  const museumSlug = query.slug as string;

  const museumOgpQuery = await apollo.query({
    query: MuseumOgpArtworkDocument,
    variables: {
      museumSlug,
      artworkId,
      locale,
    },
    context: { clientName: 'dato' },
  });

  const { museumExhibition, museumArtwork } = museumOgpQuery.data;
  const { title, booths, ogpArtworkImagePublic, thumbnailImage } =
    museumExhibition ?? {};
  const seoProps = {
    pageUrl,
    title,
    thumbnailMedia: thumbnailImage,
  };

  if (!artworkId) {
    return seoProps;
  }

  const { image } = museumArtwork;
  const { url: artworkImageUrl } = image ?? {};

  // TODO: チケット方式を今後やらないのであれば、needTicket関連の処理を取り除き、簡略化したい。
  const { booth } = findArtworkById(booths as BoothData[], artworkId) ?? {};
  const needTicket = booth?.needTicket;
  const canShareArtwork = ogpArtworkImagePublic && !needTicket;

  // MEMO: 国別にArtwork画像が登録されていない場合にエラーになるので、その場合は展覧会のサムネイル画像を表示する
  const artworkImage =
    canShareArtwork && artworkImageUrl ? artworkImageUrl : thumbnailImage.url;
  return {
    ...seoProps,
    artworkImage,
  };
};

export { createNextSeoProps, getExhibitionSeoProps };
